import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Building2, Mail, MapPin, Phone } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import type { Location } from "@/content/locations";
import { SITE } from "@/content/site";
import portrait from "@/assets/tamal-portrait-photo.webp?w=192&format=webp";

/**
 * City hub opening: the page H1 (visible on Rangpur, screen-reader-only elsewhere),
 * intro copy, calls to action and the service-area / contact card.
 */
export function CityHubHero({ loc }: { loc: Location }) {
  const isRangpur = loc.slug === "rangpur";
  return (
    <>
      {!isRangpur && (
        <h1 className="sr-only">
          SEO Expert in {loc.city}, {loc.country} — Local SEO Services
        </h1>
      )}

      <Section
        headingLevel={isRangpur ? 1 : 2}
        eyebrow={`${loc.region} · ${loc.country}`}
        title={
          <>
            SEO Expert in <span className="text-gradient">{loc.city}.</span>
          </>
        }
      >
        <div className="grid lg:grid-cols-3 gap-10 -mt-4">
          <Reveal className="lg:col-span-2">
            <p className="text-xl text-muted-foreground leading-relaxed">{loc.intro}</p>
            {isRangpur ? <RangpurHeroActions /> : <CityHeroActions city={loc.city} />}
          </Reveal>
          <Reveal delay={0.1}>
            <ServiceAreaCard loc={loc} />
          </Reveal>
        </div>
      </Section>
    </>
  );
}

function RangpurHeroActions() {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Button asChild size="lg">
        <Link to="/book">
          Discuss your Rangpur project <ArrowUpRight />
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline">
        <Link to="/free-seo-audit">
          Free website audit <ArrowUpRight />
        </Link>
      </Button>
    </div>
  );
}

function CityHeroActions({ city }: { city: string }) {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Link
        to="/contact"
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90"
      >
        Get an SEO quote for {city} <ArrowUpRight size={16} />
      </Link>
      <Link
        to="/services"
        className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
      >
        See all SEO services
      </Link>
      <Link
        to="/free-seo-audit"
        className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
      >
        Free SEO audit
      </Link>
    </div>
  );
}

function ServiceAreaCard({ loc }: { loc: Location }) {
  const isRangpur = loc.slug === "rangpur";
  return (
    <div className="rounded-3xl border border-border p-8 bg-card space-y-4">
      {isRangpur && (
        <div className="flex items-center gap-4">
          <img
            src={portrait}
            width={80}
            height={80}
            alt="Rahatul Ashiq Tamal, Rangpur-based SEO consultant"
            className="size-20 rounded-full object-cover"
          />
          <div>
            <div className="font-display text-lg">Rahatul Ashiq Tamal</div>
            <Link to="/about" className="text-sm text-primary underline underline-offset-4">
              Freelancer Tamal
            </Link>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <MapPin className="text-primary" size={18} />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Service area
          </div>
          <div className="font-display text-lg">
            {loc.city}, {loc.region}
          </div>
        </div>
      </div>
      {isRangpur ? (
        <div className="text-sm text-muted-foreground">
          {SITE.streetAddress}, {SITE.city} {SITE.postalCode}
          <p className="mt-2">Contact me to arrange a meeting before visiting.</p>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Building2 className="text-primary" size={18} />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Population
            </div>
            <div className="font-display text-lg">{loc.population}</div>
          </div>
        </div>
      )}
      <div className="border-t border-border pt-4 space-y-2 text-sm">
        <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-primary">
          <Mail size={14} /> {SITE.email}
        </a>
        <a href={`tel:${SITE.phone}`} className="flex items-center gap-3 hover:text-primary">
          <Phone size={14} /> {SITE.phone}
        </a>
      </div>
    </div>
  );
}
